import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2, XCircle } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import JobsRepository from "../../repositories/JobsRepository";
import { BulkDeletionJob, isBulkDeletionJob } from "../../models/Jobs/JobRequest";
import { JobEntityTypeLabels } from "../../models/Jobs/JobEntityType";
import { canAbortJob, canStopJob, isJobFinished } from "../../models/Jobs/JobActions";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import JobRequestStatusChip from "./JobRequestStatusChip";
import AbortJobModal from "./AbortJobModal";
import StopJobButton from "./StopJobButton";
import JobInfoView from "./JobInfoView";
import JobProgress from "./JobProgress";
import { BulkDeletionSummary } from "../common/BulkDeletionSummary";

interface BulkDeletionJobsPanelProps {
  organizationId?: string;
}

/**
 * The organization's deletions driven from an uploaded id list, across every family and both modes.
 */
/*
 * Read from one endpoint rather than the ten per-family status endpoints: a run of this kind reuses its family's
 * job type, so it would otherwise take one query per family per mode to find them all.
 */
const BulkDeletionJobsPanel: React.FC<BulkDeletionJobsPanelProps> = ({ organizationId }) => {
  const jobsRepository = new JobsRepository();
  const queryClient = useQueryClient();
  const [jobToAbort, setJobToAbort] = useState<BulkDeletionJob | null>(null);

  const jobsQuery = useQuery({
    queryKey: [QUERY_KEYS.BULK_DELETION_JOBS, organizationId],
    queryFn: async () => (await jobsRepository.getBulkDeletionJobs(organizationId!)).filter(isBulkDeletionJob),
    enabled: !!organizationId,
    refetchInterval: (query) => (query.state.data?.some((job) => !isJobFinished(job)) ? 30000 : false),
    refetchIntervalInBackground: true,
  });

  const jobs = jobsQuery.data ?? [];
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BULK_DELETION_JOBS, organizationId] });
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
  };

  return (
    <>
      {/* Fills the space under the form, but keeps room for a few runs; the page scrolls past that. */}
      <Card className="flex flex-1 flex-col overflow-hidden min-h-80 p-4">
        <div className="flex justify-between items-start mb-3">
          <div>
            <h3 className="text-sm font-semibold">Uploaded list deletions</h3>
            <p className="text-xs text-muted-foreground">
              Runs started from an uploaded list for this organization. A run reports what it could not
              delete rather than failing outright — download its report to see which ids and why.
            </p>
          </div>
          {jobsQuery.isFetching && <p className="text-xs text-muted-foreground">Refreshing...</p>}
        </div>

        <Table containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              <TableHead>Target</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Outcome</TableHead>
              {/* Pinned, so Stop and Remove stay on screen when the table is wider than the panel. */}
              <TableHead className="sticky right-0 bg-card" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {jobsQuery.isPending && (
              <TableRow>
                <TableCell colSpan={99}>
                  <Skeleton className="h-8 w-full" />
                </TableCell>
              </TableRow>
            )}
            {jobs.map((job) => (
              <TableRow key={job.id}>
                <TableCell className="text-sm">{JobEntityTypeLabels[job.entityType]}</TableCell>
                <TableCell className="text-sm text-muted-foreground whitespace-normal break-words min-w-40">
                  {job.bulk.reason}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col items-start gap-1">
                    <JobRequestStatusChip status={job.status} />
                    <JobInfoView jobRequest={job} stopOnly />
                  </div>
                </TableCell>
                <TableCell className="whitespace-normal min-w-48">
                  <div className="flex flex-col gap-1">
                    <JobProgress job={job} />
                    <BulkDeletionSummary bulk={job.bulk} />
                  </div>
                </TableCell>
                <TableCell className="sticky right-0 bg-card">
                  {canStopJob(job) ? (
                    <StopJobButton job={job} onStopped={refresh} />
                  ) : (
                    canAbortJob(job) && (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={isJobFinished(job) ? "Remove" : "Cancel"}
                        className="text-destructive"
                        onClick={() => setJobToAbort(job)}
                      >
                        {isJobFinished(job) ? <Trash2 className="size-4" /> : <XCircle className="size-4" />}
                      </Button>
                    )
                  )}
                </TableCell>
              </TableRow>
            ))}
            {jobs.length === 0 && !jobsQuery.isPending && (
              <TableRow>
                <TableCell colSpan={99} className="h-24 text-center text-muted-foreground">
                  No deletions have been started from an uploaded list.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {jobToAbort && (
        <AbortJobModal
          open={jobToAbort !== null}
          jobRequest={jobToAbort}
          onClose={() => setJobToAbort(null)}
          onConfirm={async () => {
            await jobsRepository.deleteJob(jobToAbort.id);
            await refresh();
            setJobToAbort(null);
          }}
        />
      )}
    </>
  );
};

export default BulkDeletionJobsPanel;
